import {
  cheatSheetTwsBinColumnHeader,
  isRoundupBearawayManeuverType,
} from './cheatSheetColumnLabels';
import {
  getRoundupBearawayClimaxColumns,
  ROUNDUP_BEARAWAY_SUMMARY_METRIC_CHANNELS,
  TACK_GYBE_CHEAT_SHEET_METRIC_CHANNELS,
} from '../../../utils/maneuversConfig';
import { GP50_STRAIGHT_LINE_API_HEADERS } from '../../../utils/gp50StraightLineCheatSheetChannels';
import { SPEED_UNIT_KPH, speedUnitBracketUpper, type SpeedDisplayUnit } from '../../../utils/speedUnits';

/**
 * Summary Tables column order — cheat sheet API keys (see CHEAT_SHEET_STRAIGHT_LINE_API_TO_DB).
 * `tws_avg` is the per-row mean TWS for fleet/day (one row per team).
 */
export const trainingSummaryStraightColumnOrder: readonly string[] = [
  'tws_avg',
  'bsp',
  'twa',
  'vmg',
  'vmg_percent',
  'heel_n',
  'pitch',
  'foil_rake',
  'rh_lwd',
  'db_piercing_lwd',
  'rud_rake',
  'rud_diff',
  'db_cant',
  'db_cant_eff',
  'wing_ca1',
  'wing_twist',
  'wing_clew',
  'jib_sht',
  'jib_cunno',
  'jib_lead',
];

/** Tack/gybe headers — Fleet Cheat Sheet Maneuvers section. */
const TACK_GYBE_SUMMARY_COLUMN_HEADERS: Record<string, string> = {
  bsp_drop: 'BSP DROP [KPH]',
  twa_drop: 'TWA DROP [DEG]',
  drop_time: 'DROP TIME [S]',
  turn_rate_max: 'TURN RATE MAX [DEG/SEC]',
  bsp_min: 'BSP MIN [KPH]',
  bsp_min_delta: 'BSP MIN DELTA [KPH]',
  twa_exit: 'TWA EXIT [DEG]',
  turn_angle_max: 'TURN ANGLE MAX [DEG]',
  rake_aoa_min_old_turn: 'RAKE AOA MIN [DEG]',
  overshoot_angle: 'OVERSHOOT ANGLE [DEG]',
  raise_time: 'RAISE TIME [S]',
  time_two_boards: 'TWO BOARD TIME [S]',
  twa_build: 'TWA BUILD [DEG]',
  loss_inv_tgt: 'LOSS INV [M]',
  loss_turn_tgt: 'LOSS TURN [M]',
  loss_build_tgt: 'LOSS BUILD [M]',
  loss_total_tgt: 'LOSS TOTAL [M]',
  vmg_perc_avg: 'VMG [%]',
};

/** Roundup/bearaway headers — maneuvers big table (getBigTableColumns). */
const ROUNDUP_BEARAWAY_SUMMARY_BASE_HEADERS: Record<string, string> = {
  loss_inv_tgt: 'INV LOSS [M]',
  loss_turn_tgt: 'TURN LOSS [M]',
  loss_build_tgt: 'BUILD LOSS [M]',
  loss_total_tgt: 'TOTAL LOSS [M]',
  mmg: 'MMG [M]',
  vmg_perc_avg: 'VMG%',
  bsp_entry: 'BSP ENTRY [KPH]',
  bsp_max: 'MAX BSP [KPH]',
  bsp_min: 'BS MIN [KPH]',
  bsp_min_delta: 'BS MIN Δ [KPH]',
  turn_rate_max: 'TURN RATE [DEG/SEC]',
  turn_radius: 'TURN RADIUS [M]',
  twa_entry: 'TWA ENTRY [DEG]',
  twa_exit: 'TWA EXIT [DEG]',
  time_two_boards: 'TWO BOARD TIME [S]',
};

function roundupBearawayClimaxSummaryHeaders(maneuverType: string): Record<string, string> {
  return Object.fromEntries(
    getRoundupBearawayClimaxColumns(maneuverType).map((c) => [c.channel.toLowerCase(), c.header])
  );
}

/** Maneuver keys aligned with Fleet Cheat Sheet Maneuvers column order. */
export const trainingSummaryManeuverColumnOrder: readonly string[] = [
  'tws_avg',
  ...TACK_GYBE_CHEAT_SHEET_METRIC_CHANNELS,
];

export function trainingSummaryManeuverColumnOrderForType(
  maneuverType: string
): readonly string[] {
  if (isRoundupBearawayManeuverType(maneuverType)) {
    return ['tws_avg', ...ROUNDUP_BEARAWAY_SUMMARY_METRIC_CHANNELS];
  }
  return ['tws_avg', ...TACK_GYBE_CHEAT_SHEET_METRIC_CHANNELS];
}

export function trainingSummaryStraightColumnHeaders(
  unit: SpeedDisplayUnit = SPEED_UNIT_KPH
): Record<string, string> {
  const twsHeader = cheatSheetTwsBinColumnHeader(unit);
  const U = speedUnitBracketUpper(unit);
  return {
    ...GP50_STRAIGHT_LINE_API_HEADERS,
    bsp: `BSP ${U}`,
    vmg: `VMG ${U}`,
    tws_avg: twsHeader,
    tws: twsHeader,
    vmg_percent: 'VMG%',
    vmg_perc: 'VMG%',
    source_name: 'TEAM',
  };
}

export function trainingSummaryManeuverColumnHeaders(
  unit: SpeedDisplayUnit,
  maneuverType?: string
): Record<string, string> {
  const twsHeader = cheatSheetTwsBinColumnHeader(unit);
  const U = speedUnitBracketUpper(unit);
  const speedHeaders = {
    tws_avg: twsHeader,
    tws: twsHeader,
    bsp_drop: `BSP DROP ${U}`,
    bsp_min: `BSP MIN ${U}`,
    bsp_min_delta: `BSP MIN DELTA ${U}`,
    bsp_entry: `BSP ENTRY ${U}`,
    bsp_max: `MAX BSP ${U}`,
  };
  if (maneuverType && isRoundupBearawayManeuverType(maneuverType)) {
    return {
      ...TACK_GYBE_SUMMARY_COLUMN_HEADERS,
      ...ROUNDUP_BEARAWAY_SUMMARY_BASE_HEADERS,
      ...roundupBearawayClimaxSummaryHeaders(maneuverType),
      ...speedHeaders,
    };
  }
  return {
    ...TACK_GYBE_SUMMARY_COLUMN_HEADERS,
    ...ROUNDUP_BEARAWAY_SUMMARY_BASE_HEADERS,
    ...roundupBearawayClimaxSummaryHeaders('BEARAWAY'),
    ...speedHeaders,
  };
}

export function trainingSummaryStraightTableHeader(key: string, unit: SpeedDisplayUnit): string {
  const k = key.toLowerCase();
  if (k === 'source_name') {return 'TEAM';}
  return trainingSummaryStraightColumnHeaders(unit)[k] ?? key;
}

export function trainingSummaryManeuverTableHeader(
  key: string,
  unit: SpeedDisplayUnit,
  maneuverType?: string
): string {
  const k = key.toLowerCase();
  if (k === 'source_name') {return 'TEAM';}
  return trainingSummaryManeuverColumnHeaders(unit, maneuverType)[k] ?? key;
}

/**
 * Acceleration (build-phase) keys — Fleet Summary Acceleration section.
 * `*_accmax` = value at max build acceleration on maneuver exit (mirrors Cheat Sheet accelerations).
 */
export const trainingSummaryAccelerationColumnOrder: readonly string[] = [
  'tws_avg',
  'vmg_perc_build',
  'bsp_accmax',
  'twa_accmax',
  'cant_accmax',
  'cant_eff_accmax',
  'pitch_accmax',
  'heel_accmax',
  'jib_sheet_pct_accmax',
  'jib_lead_ang_accmax',
  'jib_cunno_load_accmax',
  'wing_clew_pos_accmax',
  'wing_twist_accmax',
  'wing_ca1_accmax',
  'rake_accmax',
  'rud_rake_accmax',
  'rud_diff_accmax',
];

export function trainingSummaryAccelerationColumnHeaders(
  unit: SpeedDisplayUnit = SPEED_UNIT_KPH
): Record<string, string> {
  const twsHeader = cheatSheetTwsBinColumnHeader(unit);
  const U = speedUnitBracketUpper(unit);
  return {
    tws_avg: twsHeader,
    tws: twsHeader,
    source_name: 'TEAM',
    vmg_perc_build: 'VMG [%]',
    bsp_accmax: `BSP ${U}`,
    twa_accmax: 'TWA [DEG]',
    cant_accmax: 'DB CANT [DEG]',
    cant_eff_accmax: 'DB CANT EFF [DEG]',
    pitch_accmax: 'PITCH [DEG]',
    heel_accmax: 'HEEL [DEG]',
    jib_sheet_pct_accmax: 'JIB SHT [%]',
    jib_lead_ang_accmax: 'JIB LEAD [DEG]',
    jib_cunno_load_accmax: 'JIB CUNNO [KGF]',
    wing_clew_pos_accmax: 'WING CLEW [MM]',
    wing_twist_accmax: 'WING TWIST [DEG]',
    wing_ca1_accmax: 'WING CA1 [DEG]',
    rake_accmax: 'RAKE [DEG]',
    rud_rake_accmax: 'RUD RAKE [DEG]',
    rud_diff_accmax: 'RUD DIFF [DEG]',
  };
}

export function trainingSummaryAccelerationTableHeader(key: string, unit: SpeedDisplayUnit): string {
  const k = key.toLowerCase();
  if (k === 'source_name') {return 'TEAM';}
  return trainingSummaryAccelerationColumnHeaders(unit)[k] ?? key;
}
